import React, { useContext, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { DataGridPro, GRID_STRING_COL_DEF } from '@mui/x-data-grid-pro';
import CheckIcon from '@mui/icons-material/Check';
import WarningTriangle from '../../../components/WarningTriangle';
import { Button, Typography, Backdrop } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import { GBENSContext } from '../GBENSContainer';
import { StoreContext } from '../../../components/Store';
import {
  SubmitModal,
  DeleteConsignmentModal,
  DuplicateModal,
  CannotCancelShipmentModal,
  CancelShipmentModal,
  AmendShipmentModal,
  SoftErrorModal,
} from '../Modals/MenuBarModals';

import { SubmitENS } from '../../../api/GBENSDec';

function StatusCell({ value }) {
  if (!value?.errors) return <CheckIcon sx={{ color: 'green' }} />;
  return (
    <WarningTriangle
      message={value?.message}
      errors={Object.keys(value?.errors ?? {})
        .filter((k) => k !== 'lines' && k !== 'id')
        .map((k) => value?.errors[k])}
    />
  );
}

StatusCell.propTypes = {
  value: PropTypes.object,
};

const statusColumnType = {
  ...GRID_STRING_COL_DEF,
  type: 'custom',
  resizable: false,
  filterable: false,
  sortable: false,
  editable: false,
  groupable: false,
  display: 'flex',
  renderCell: (params) => <StatusCell {...params} />,
};

const ReviewAndSubmit = () => {
  const [clientJobNumber, setClientJobNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);

  const [showSoftErrorModal, setShowSoftErrorModal] = useState(false);
  const [softErrorMessage, setSoftErrorMessage] = useState('');

  const {
    headerId,
    deleteConsignmentModalOpen,
    setDeleteConsignmentModalOpen,
    duplicateModalOpen,
    setDuplicateModalOpen,
    setCancelShipmentModalOpen,
    cancelShipmentModalOpen,
    cannotCancelShipmentModalOpen,
    setCannotCancelShipmentModalOpen,
    submitModalOpen,
    setSubmitModalOpen,
    data,
    referenceData,
    errors,
    allValid,
    isLocked,
    isAccompanied,
    cancelOrDelete,
    amendShipmentModalOpen,
    setAmendShipmentModalOpen,
    isReadOnly,
    showSubmitButton,
  } = useContext(GBENSContext);

  const { hasSufficientBalance } = useContext(StoreContext);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const rtrnValue = await SubmitENS(headerId);

      if (rtrnValue?.isValid === 0 && rtrnValue?.message) {
        setSoftErrorMessage(rtrnValue.message);
        setShowSoftErrorModal(true);
        setIsSubmitting(false);
        setHasSubmitError(false);
        return;
      }

      setClientJobNumber(rtrnValue?.clientJobNumber);
      setHasSubmitError(false);
      setSubmitModalOpen(true);
    } catch (error) {
      console.error('Error submitting ENS:', error);
      setIsSubmitting(false);

      setHasSubmitError(true);
      setSubmitModalOpen(true);
    }
  };

  const shipmentStatus = useMemo(() => {
    if (!data?.TransportAndJourney?.statusDescription) return 'Valid';
    if (data?.TransportAndJourney?.statusDescription?.toUpperCase?.() === 'DRAFT' && allValid) return 'Valid';
    return data?.TransportAndJourney?.statusDescription;
  }, [data?.TransportAndJourney?.statusDescription]);

  const accompaniedName = useMemo(() => {
    // if ROAD transport then we want to display ROAD
    // otherwise want to display ACCOMPANIED RORO
    const transportModeId = data?.TransportAndJourney?.Vehicle?.ModeOfTransport;
    const transportModeName = referenceData?.modeOfTransport?.find(
      (m) => parseInt(m.key) === parseInt(transportModeId)
    )?.value;

    return transportModeName === 'ROAD' ? 'a ROAD' : 'an ACCOMPANIED RORO';
  }, []);

  //---Header Table-----
  const rowsHeader = [
    {
      id: 1,
      reference: data?.TransportAndJourney?.jobReference,
      vehicleID: data?.TransportAndJourney?.Vehicle?.vehicleID,
      departureDate: data.TransportAndJourney?.departureDateTime?.format?.('DD/MM/YYYY'),
      arrivalDate: data.TransportAndJourney?.arrivalDateTime?.format?.('DD/MM/YYYY'),
      noConsignments: data.Consignments?.length,
      status: shipmentStatus,
      movementReferenceNumber: data?.TransportAndJourney?.movementReferenceNumber,
    },
  ];
  const columnsHeader = [
    { field: 'reference', headerName: 'Reference', flex: 1, headerClassName: 'table-header' },
    { field: 'vehicleID', headerName: 'Vehicle Identity', flex: 1, headerClassName: 'table-header' },
    { field: 'departureDate', headerName: 'Departure Date', headerClassName: 'table-header', flex: 1 },
    { field: 'arrivalDate', headerName: 'Arrival Date', headerClassName: 'table-header', flex: 1 },
    { field: 'noConsignments', headerName: 'Number of Consignments', headerClassName: 'table-header', flex: 1 },
    ...(!isAccompanied
      ? [{ field: 'movementReferenceNumber', headerName: 'MRN', headerClassName: 'table-header', flex: 1 }]
      : []),
    { field: 'status', headerName: 'Status', headerClassName: 'table-header', flex: 1 },
  ];

  //---Consignment Table-----
  const rowsConsignments = [...(data?.Consignments ?? [])].map((c) => ({
    id: c?.id,
    reference: c?.localReferenceNumber || 'n/a',
    consignmentReference: c?.consignmentReference || 'n/a',
    consignmentNumber: c?.consignmentNumber ?? 'n/a',
    consignor: c?.Consignor?.companyName,
    consignee: c?.Consignee?.companyName,
    numberOfLines: c?.lines?.length || 0,
    mrn: c?.mrn,
    status: {
      message: 'There are errors with this consignment.',
      errors:
        errors?.Consignments?.find?.(
          (e) => e.id === c.id && Object.keys(e).filter((k) => k !== 'id' && k !== 'lines').length > 0
        ) ?? null,
    },
  }));

  const columnsConsignments = [
    {
      field: 'consignmentNumber',
      headerName: '#',
      headerClassName: 'table-header',
      flex: 1,
      width: 75,
      minWidth: 75,
      maxWidth: 75,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'reference', headerName: 'Reference', headerClassName: 'table-header', flex: 1 },
    { field: 'consignor', headerName: 'Consignor', headerClassName: 'table-header', flex: 1 },
    { field: 'consignee', headerName: 'Consignee', headerClassName: 'table-header', flex: 1 },
    { field: 'consignmentReference', headerName: 'Consignment Reference', headerClassName: 'table-header', flex: 1 },
    { field: 'numberOfLines', headerName: 'Number of Lines', headerClassName: 'table-header', flex: 1 },
    ...(isAccompanied ? [{ field: 'mrn', headerName: 'MRN', headerClassName: 'table-header', flex: 1 }] : []),
    { field: 'status', headerName: 'Status', headerClassName: 'table-header', flex: 1, ...statusColumnType },
  ];

  const submitMessage = () => {
    if (isLocked) return 'Your ENS data is locked and cannot be submitted.';
    if (!hasSufficientBalance)
      return 'Your current balance is 0 units. You are unable to submit to Customs until you have purchased additional units.';
    return allValid
      ? 'Your ENS data is valid and can be submitted to ICS2.'
      : 'Your ENS data is incomplete. Please review the steps and update the highlighted fields.';
  };

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSubmitting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SubmitModal
        clientJobNumber={clientJobNumber}
        open={submitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        hasError={hasSubmitError}
      />
      <DeleteConsignmentModal open={deleteConsignmentModalOpen} onClose={() => setDeleteConsignmentModalOpen(false)} />
      <DuplicateModal open={duplicateModalOpen} onClose={() => setDuplicateModalOpen(false)} headerId={headerId} />
      <CancelShipmentModal
        open={cancelShipmentModalOpen}
        onClose={() => setCancelShipmentModalOpen(false)}
        headerId={headerId}
        reference={rowsHeader[0]?.reference}
        cancelOrDelete={cancelOrDelete}
      />
      <AmendShipmentModal
        open={amendShipmentModalOpen}
        onClose={() => setAmendShipmentModalOpen(false)}
        headerId={headerId}
      />
      <CannotCancelShipmentModal
        open={cannotCancelShipmentModalOpen}
        onClose={() => setCannotCancelShipmentModalOpen(false)}
      />
      <SoftErrorModal
        open={showSoftErrorModal}
        onClose={() => setShowSoftErrorModal(false)}
        message={softErrorMessage}
      />

      <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
        <Typography id="modal-modal-title" variant="h6" component="h6" style={{ marginLeft: 0, marginBottom: 30 }}>
          {isAccompanied
            ? `This is ${accompaniedName} Declaration, all consignments must receive an individual MRN.`
            : 'This is an UNACCOMPANIED RORO Declaration, all consignments are accepted on one MRN.'}
        </Typography>

        <DataGridPro
          hideFooter
          rows={rowsHeader}
          columns={columnsHeader}
          style={{ marginBottom: 30, background: '#fff' }}
        />

        <DataGridPro
          initialState={{
            pagination: {
              paginationModel: { pageSize: 15 },
            },
          }}
          style={{ background: '#fff' }}
          pageSizeOptions={[15, 25, 50, 100]}
          pagination
          rows={rowsConsignments}
          columns={columnsConsignments}
        />
      </div>
      {showSubmitButton && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 30,
            paddingLeft: 30,
            paddingRight: 30,
            marginTop: 20,
          }}
        >
          <div>{submitMessage()}</div>
          <Button
            variant="outlined"
            onClick={handleSubmit}
            disabled={isReadOnly || !allValid || isLocked || !hasSufficientBalance}
          >
            Submit
          </Button>
        </div>
      )}
    </>
  );
};

export default ReviewAndSubmit;
